import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  LOCALE_ID,
  OnInit,
  Output,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormGroup, FormBuilder, FormArray, Validators } from '@angular/forms';
import { forkJoin, of } from 'rxjs';
import { catchError, map, switchMap, takeWhile } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { OrdersBaseComponent } from '@components/admin/managed-board/base/orders-base/orders-base.component';
import { OrderForm } from '@classes/order-form';
import { Order, LogElement } from '@models/order';
import { Patient } from '@models/patient';
import { Store } from '@models/store';
import { ProductElement } from '@models/product';
import { Vendor } from '@models/vendor';
import { AuthService } from '@services/auth.service';
import { NotificationService } from '@services/notification.service';
import { OrderService } from '@services/order.service';
import { PatientService } from '@services/patient.service';
import { ProductService } from '@services/product.service';
import { StoreService } from '@services/store.service';
import { VendorOrderService } from '@services/vendor-order.service';
import { PaymentService } from '@services/payment.service';
import { FinancialCalculator } from '@classes/financial';
import { ReturnOrderDialogComponent, ReturnOrderDialogResult } from '@components/admin/patient/return-order-dialog/return-order-dialog.component';
import { RestyleOrderDialogComponent } from '@components/admin/patient/restyle-order-dialog/restyle-order-dialog.component';
import { HoldFollowUpDialogComponent } from '@components/admin/patient/hold-follow-up-dialog/hold-follow-up-dialog.component';
import { InternalNoteDialogComponent } from '@components/admin/patient/internal-note-dialog/internal-note-dialog.component';
import { RepairOrderDialogComponent } from '@components/admin/patient/repair-order-dialog/repair-order-dialog.component';
import { RepairJob } from '@classes/repair-job';
import { DuplicateJob } from '@classes/duplicate-job';
import { ExchangeJob } from '@classes/exchange-job';
import { NewRxJob } from '@classes/new-rx-job';
import { RedoJob } from '@classes/redo-job';
import { ReturnJob } from '@classes/return-job';
import { RedoOrderDialogComponent } from '@components/admin/patient/redo-order-dialog/redo-order-dialog.component';
import { EmailOrderDialogComponent } from '@components/admin/patient/email-order-dialog/email-order-dialog.component';
import { DeleteOrderDialogComponent, DeleteOrderDialogResult } from '@components/admin/patient/delete-order-dialog/delete-order-dialog.component';
import { OrderDocument } from '@classes/order-document';
import {
  FIRST_INSPECTION_NEEDED,
  ORDER_LENSES_ACTION,
  combinedAwaitingStatus,
  exclusiveJobAction,
  hasJobAction,
  isOpenTaskStatus,
  jobActionList,
  statusAfterFrameReceived,
  statusAfterLensesReceived,
  toggleJobAction,
  withExclusiveJobAction,
} from '@classes/job-actions';

@Component({
  selector: 'app-patient-order-details',
  templateUrl: './patient-order-details.component.html',
  styleUrls: ['./patient-order-details.component.css'],
})
export class PatientOrderDetailsComponent
  extends OrdersBaseComponent
  implements OnInit
{
  /** When set, load this order instead of reading route params (dialog/embed). */
  @Input() embedPatientId: string;
  @Input() embedOrderId: string;
  @Input() embedded = false;
  @Output() closeEmbed = new EventEmitter<{ saved?: boolean }>();

  patient: Patient;
  orderFormGroup: FormGroup;
  loaded = false;
  isNewOrder = false;
  patientId: string;
  currentStore: Store;
  panelOpenStateFrameDetails = true;
  panelOpenStatePrescription = false;
  panelOpenStateLens = false;
  panelOpenStateFinancial = false;
  panelOpenStateRepair = false;
  private orderChangedInEmbed = false;

  get hasInternalNote(): boolean {
    return !!String(this.orderFormGroup?.get('notes')?.value || '').trim();
  }

  get orderStatusLabel(): string {
    const status = String(this.orderFormGroup?.get('status')?.value || '').trim();
    if (status) {
      return status;
    }
    if (String(this.orderFormGroup?.get('redoReason')?.value || '').trim()) {
      return 'RE-DO';
    }
    return '—';
  }

  get isRedoJob(): boolean {
    return !!String(this.orderFormGroup?.get('redoReason')?.value || '').trim();
  }

  get isReturnJob(): boolean {
    const status = String(this.orderFormGroup?.get('status')?.value || '').trim();
    return status === 'RETURNED' || this.hasJob('RETURN');
  }

  get isPriorityRedo(): boolean {
    return !!this.orderFormGroup?.get('redoPriority')?.value;
  }

  get isQuoteDisplay(): boolean {
    if (this.hasJob('QUOTE')) {
      return true;
    }
    const status = String(this.orderFormGroup?.get('status')?.value || '').trim();
    const todo = String(this.orderFormGroup?.get('todo')?.value || '').trim();
    const isQuoteRecord = status === 'QUOTE' || todo === 'QUOTE';
    if (!isQuoteRecord) {
      return false;
    }
    return !this.invoiceNumberDisplay || this.hasJob('CONVERT TO ORDER');
  }

  get invoiceNumberDisplay(): string {
    return String(this.orderFormGroup?.get('invoiceNumber')?.value || '').trim();
  }

  get isShipFulfillment(): boolean {
    const status = String(this.orderFormGroup?.get('status')?.value || '').trim();
    return (
      String(this.orderFormGroup?.get('fulfillment')?.value || '') === 'SHIP' &&
      status !== 'DISPENSED' &&
      status !== 'VOID'
    );
  }

  constructor(
    @Inject(LOCALE_ID) public locale: string,
    public auth: AuthService,
    public orderService: OrderService,
    public vendorOrderService: VendorOrderService,
    public productService: ProductService,
    public storeService: StoreService,
    public cdRef: ChangeDetectorRef,
    public fb: FormBuilder,
    public route: ActivatedRoute,
    public notificationService: NotificationService,
    private patientService: PatientService,
    private paymentService: PaymentService,
    private router: Router,
    private dialog: MatDialog
  ) {
    super(
      locale,
      auth,
      orderService,
      vendorOrderService,
      productService,
      storeService,
      cdRef,
      fb,
      route,
      notificationService
    );
  }

  ngOnInit(): void {
    this.getAuthorized();
    this.currentStore = this.auth.getStoreObject();
    this.auth.getStoreObjectMessage().subscribe((store: Store) => {
      this.currentStore = store;
    });

    if (this.embedded && this.embedPatientId && this.embedOrderId) {
      this.loadOrder(this.embedPatientId, this.embedOrderId);
      return;
    }

    this.route.paramMap
      .pipe(
        switchMap((params) => {
          this.patientId = params.get('id');
          const orderId = params.get('orderId');
          this.isNewOrder = orderId === 'new';

          return forkJoin({
            patient: this.patientService.getPatient(this.patientId),
            order: this.isNewOrder
              ? this.createNewOrderWithReservedInvoice()
              : this.orderService.getOrderById(orderId),
          });
        })
      )
      .subscribe(({ patient, order }) => {
        this.applyLoadedOrder(patient, order);
      });
  }

  private loadOrder(patientId: string, orderId: string): void {
    this.patientId = patientId;
    this.isNewOrder = orderId === 'new';
    forkJoin({
      patient: this.patientService.getPatient(patientId),
      order: this.isNewOrder
        ? this.createNewOrderWithReservedInvoice()
        : this.orderService.getOrderById(orderId),
    }).subscribe(({ patient, order }) => {
      this.applyLoadedOrder(patient, order);
    });
  }

  private createNewOrderWithReservedInvoice() {
    return this.orderService.reserveInvoiceNumber().pipe(
      map((invoiceNumber) => {
        const order = this.createNewOrder();
        order.invoiceNumber = invoiceNumber;
        return order;
      }),
      catchError((err) => {
        console.error('reserveInvoiceNumber failed', err);
        this.notificationService.show(
          'error',
          'Could not reserve invoice number. You can still save after fixing connectivity.',
          'OK'
        );
        return of(this.createNewOrder());
      })
    );
  }

  private applyLoadedOrder(patient: Patient, order: Order): void {
    this.patient = patient;
    this.orderFormGroup = OrderForm.initOrderFormGroup(
      order,
      this.auth,
      this.storeOptions
    );
    this.orderFormGroup.get('products.0.duty')?.enable();
    this.ensureOrderDateDefaultsToToday();
    this.autofillInitials();
    this.loaded = true;
    this.cdRef.detectChanges();
  }

  createNewOrder(): Order {
    const newProduct = new ProductElement();
    newProduct.quantity = 1;
    newProduct.frame.frameSource = 'PATIENT';
    newProduct.product.vendor = new Vendor();
    newProduct.product.vendor.images = null;
    newProduct.product.store = this.auth.getStoreObject();

    const newOrder = new Order();
    newOrder.patient = this.patientId;
    newOrder.status = '';
    newOrder.type = 'O';
    // mat-datepicker needs a Date object (not a formatted string) to display
    newOrder.date = new Date() as any;
    newOrder.user = this.auth.currentUser._id;
    newOrder.store = this.auth.getStore();
    newOrder.fulfillment = 'PICKUP';
    newOrder.products.push(newProduct);

    return newOrder;
  }

  private ensureOrderDateDefaultsToToday(): void {
    const dateControl = this.orderFormGroup?.get('date');
    if (!dateControl) {
      return;
    }

    const value = dateControl.value;
    if (value instanceof Date && !isNaN(value.getTime())) {
      return;
    }

    if (value) {
      const parsed = new Date(value);
      if (!isNaN(parsed.getTime())) {
        dateControl.setValue(parsed, { emitEvent: false });
        return;
      }
    }

    if (this.isNewOrder || !value) {
      dateControl.setValue(new Date(), { emitEvent: false });
    }
  }

  openInternalNoteDialog(): void {
    const notesControl = this.orderFormGroup?.get('notes');
    if (!notesControl) {
      return;
    }

    const dialogRef = this.dialog.open(InternalNoteDialogComponent, {
      width: '520px',
      data: { notes: notesControl.value || '' },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === undefined) {
        return;
      }
      notesControl.setValue(String(result || ''));
      notesControl.markAsDirty();
      this.cdRef.detectChanges();
    });
  }

  onSave(destination: string): void {
    if (destination === 'PRINT / EMAIL' || destination === 'PRINT') {
      this.printCompletedOrder();
      return;
    }

    if (destination === 'EMAIL') {
      this.emailCompletedOrder();
      return;
    }

    if (destination === 'DELETE') {
      this.deleteCompletedOrder();
      return;
    }

    if (destination === 'RE-DO') {
      this.createRedoFromDispensed();
      return;
    }

    this.ensureInitialsFilled();

    if (destination === 'RETURN') {
      this.returnOrder();
      return;
    }

    if (destination === 'EXCHANGE') {
      this.exchangeOrder();
      return;
    }

    if (destination === 'RE-STYLE') {
      this.restyleOrder();
      return;
    }

    if (destination === 'NEW RX') {
      this.createNewRxFromDispensed();
      return;
    }

    if (destination === 'REPAIR') {
      this.createRepairFromDispensed();
      return;
    }

    if (destination === 'DUPLICATE') {
      this.createDuplicateFromDispensed();
      return;
    }

    if (destination === 'VOID') {
      this.voidOrder();
      return;
    }

    if (destination === 'ADD TO CART') {
      this.addToCart('DISPENSED');
      return;
    }

    if (destination === 'SHIPPED') {
      this.addToCart('SHIPPED');
      return;
    }

    if (destination === 'CONVERT TO ORDER') {
      destination = this.routingDestinationFromPhysicalStatus();
    }

    destination = this.resolveCallHoldDestination(destination);

    if (this.isQuoteJob() && destination !== 'SAVE/QUOTE') {
      this.convertQuoteToOrder(() => this.saveWithDestination(destination));
      return;
    }

    this.saveWithDestination(destination);
  }

  private saveWithDestination(destination: string): void {
    const newOrder = this.orderFormGroup.get('products.0._id').value == '';
    const alreadyHold = this.isHoldRxJob();
    this.syncFrameOnlyLog();
    if (newOrder && destination !== 'SAVE/QUOTE' && !this.isQuoteJob()) {
      this.ensureActivityEntry('JOB CREATED');
    }
    this.applyDestinationMetadata(destination);

    const persist = () => this.persistOrder(newOrder, destination);

    if (destination === 'SAVE/HOLD') {
      const go = () => {
        if (this.shouldDeductEnclosedInventory()) {
          this.deductEnclosedInventory(persist);
          return;
        }
        persist();
      };
      if (!alreadyHold) {
        this.confirmHoldFollowUp((confirmed) => {
          if (confirmed) {
            go();
          }
        });
        return;
      }
      go();
      return;
    }

    if (destination === 'SAVE/QUOTE') {
      persist();
      return;
    }

    if (destination === 'TO REPAIR') {
      const items =
        this.orderFormGroup.get('products.0.frame.repairItems')?.value || [];
      if (!Array.isArray(items) || !items.length) {
        this.promptRepairItems((selected) => {
          if (!selected?.length) {
            return;
          }
          this.orderFormGroup.get('products.0.frame.repairItems')?.setValue(selected);
          this.applyDestinationMetadata(destination);
          persist();
        });
        return;
      }
    }

    if (destination == 'ADDED TO INVENTORY') {
      const invoiceNumber = this.orderFormGroup.get('invoiceNumber').value;
      this.orderFormGroup.get('invoiceNumber').setValue(invoiceNumber + '-C');
      this.orderFormGroup.get('status').setValue('CANCELED');

      this.productService
        .incrementProductInventoryQuantity(
          this.orderFormGroup.get('products.0.product').value,
          1
        )
        .subscribe((incrementProduct) => {
          if (!this.orderFormGroup.get('products.0.product._id').value) {
            this.orderFormGroup
              .get('products.0.product._id')
              .setValue(incrementProduct.product._id);

            this.vendorOrderService
              .addProductIdToVendorOrder(this.orderFormGroup.value)
              .subscribe(() => {});
          }
          this.persistOrder(newOrder, destination);
        });
      return;
    }

    if (this.shouldDeductEnclosedInventory()) {
      this.deductEnclosedInventory(persist);
      return;
    }

    persist();
  }

  private applyDestinationMetadata(destination: string): void {
    const frameSourceControl = this.orderFormGroup.get('products.0.frame.frameSource');
    const todoControl = this.orderFormGroup.get('todo');
    const statusControl = this.orderFormGroup.get('status');
    const orderType = this.orderFormGroup.get('type')?.value;

    switch (destination) {
      case 'ORDERED FRAME':
      case 'ORDERED': {
        const fromStack = this.isStackToOrderStage();
        todoControl.setValue('ORDERED');
        frameSourceControl.setValue('PATIENT');
        if (this.hasJob(ORDER_LENSES_ACTION)) {
          this.orderFormGroup.get('lensStatus')?.setValue('ORDERED');
        }
        statusControl.setValue(this.getAwaitingStatusForType(orderType));
        this.orderFormGroup.get('products.0.frame.physicalStatus')?.setValue('ORDERED');
        this.removeJobActions(['SEND TO STACK']);
        if (fromStack) {
          const orderedDate = new Date();
          this.orderFormGroup.get('date')?.setValue(orderedDate);
          this.orderFormGroup.get('products.0.frame.orderDate')?.setValue(orderedDate);
        }
        this.appendFrameActivity(
          'ORDERED',
          String(
            this.orderFormGroup.get('products.0.frame.referenceNumber')?.value ||
              ''
          ).trim(),
          { eta: this.orderFormGroup.get('eta')?.value }
        );
        if (!fromStack) {
          this.ensureActivityEntry('AWAITING FRAME');
        }
        break;
      }
      case 'ORDERED LENSES':
        todoControl.setValue('ORDERED');
        this.orderFormGroup.get('lensStatus')?.setValue('ORDERED');
        statusControl.setValue(this.getAwaitingStatusForType(orderType));
        this.appendFrameActivity('ORDERED LENSES');
        break;
      case 'ORDERED ON APPROVAL':
        todoControl.setValue('ORDERED ON APPROVAL');
        frameSourceControl.setValue('PATIENT APPROVAL');
        statusControl.setValue(this.getAwaitingStatusForType(orderType));
        this.orderFormGroup.get('products.0.frame.physicalStatus')?.setValue('ON-APPROVAL');
        this.appendFrameActivity(
          'ORDERED',
          String(
            this.orderFormGroup.get('products.0.frame.referenceNumber')?.value || ''
          ).trim(),
          { eta: this.orderFormGroup.get('eta')?.value }
        );
        this.ensureActivityEntry('AWAITING FRAME');
        break;
      case 'RECEIVED FRAME': {
        const received = new Date();
        this.orderFormGroup.get('products.0.frame.dateReceived')?.setValue(received);
        this.appendFrameActivity('RECEIVED');
        const physical = String(
          this.orderFormGroup.get('products.0.frame.physicalStatus')?.value || ''
        );
        this.applyOnApprovalReceivedFromJob();
        if (physical === 'ORDERED') {
          this.orderFormGroup.get('products.0.frame.physicalStatus')?.setValue('');
        }
        if (physical !== 'ON-APPROVAL') {
          statusControl.setValue(
            statusAfterFrameReceived({
              jobAction: this.orderFormGroup.get('jobAction')?.value,
              jobActions: this.currentJobActions,
              lensStatus: this.orderFormGroup.get('lensStatus')?.value,
            })
          );
        }
        break;
      }
      case 'RECEIVED LENSES': {
        this.orderFormGroup.get('lensStatus')?.setValue('RECEIVED');
        this.appendFrameActivity('RECEIVED LENSES');
        this.appendFrameActivity(FIRST_INSPECTION_NEEDED, '', {
          keepActivityStatus: true,
        });
        statusControl.setValue(
          statusAfterLensesReceived({
            type: orderType,
            status: statusControl.value,
            physicalStatus: this.orderFormGroup.get('products.0.frame.physicalStatus')
              ?.value,
          })
        );
        break;
      }
      case 'SENT FOR REPAIR': {
        todoControl.setValue('REPAIR');
        statusControl.setValue('AWAITING REPAIR');
        this.appendFrameActivity(
          'SENT FOR REPAIR',
          String(
            this.orderFormGroup.get('products.0.frame.referenceNumber')?.value || ''
          ).trim(),
          {
            labVendor: String(
              this.orderFormGroup.get('products.0.frame.labVendor')?.value || ''
            ).trim(),
            eta: this.orderFormGroup.get('eta')?.value,
          }
        );
        this.orderFormGroup.get('products.0.frame.activityStatus')?.setValue(
          'SENT FOR REPAIR'
        );
        statusControl.markAsDirty();
        break;
      }
      case 'TO REPAIR': {
        RepairJob.applyRepairMetadata(
          this.orderFormGroup.getRawValue(),
          this.orderFormGroup.get('products.0.frame.repairItems')?.value || []
        );
        this.orderFormGroup.get('status')?.setValue('TO REPAIR');
        this.orderFormGroup.get('todo')?.setValue('REPAIR');
        this.setExclusiveJobAction('REPAIR');
        this.orderFormGroup.get('products.0.frame.pof')?.setValue(true);
        this.orderFormGroup.get('products.0.frame.physicalStatus')?.setValue('ENCLOSED');
        this.orderFormGroup.get('products.0.frame.preOrderStatus')?.setValue('ENCLOSED');
        this.orderFormGroup.get('products.0.frame.enclosed')?.setValue(true);
        this.appendFrameActivity('REPAIR / ENCLOSED');
        const items = (
          this.orderFormGroup.get('products.0.frame.repairItems')?.value || []
        ).filter(Boolean);
        if (items.length) {
          this.ensureActivityEntry(
            'REPAIR',
            `WHAT NEEDS REPAIR: ${items.join(', ')}`
          );
        }
        break;
      }
      case 'SEND TO STACK':
        todoControl.setValue('ORDER');
        frameSourceControl.setValue(
          frameSourceControl.value === 'PATIENT APPROVAL'
            ? 'PATIENT APPROVAL'
            : 'PATIENT'
        );
        if (this.isEnclosedFrame()) {
          statusControl.setValue(this.getStackOrderStatusForType(orderType));
        } else {
          this.orderFormGroup.get('date')?.setValue(new Date());
          statusControl.setValue('TO ORDER');
          this.appendFrameActivity('TO ORDER');
          this.orderFormGroup.get('products.0.frame.activityStatus')?.setValue(
            'TO ORDER'
          );
        }
        if (this.orderFormGroup.get('rxStatus')?.value === 'CALL FOR RX') {
          this.orderFormGroup.get('rxStatus')?.setValue('');
        }
        statusControl.markAsDirty();
        break;
      case 'SAVE/HOLD':
        todoControl.setValue('HOLD');
        frameSourceControl.setValue(
          frameSourceControl.value === 'PATIENT APPROVAL'
            ? 'PATIENT APPROVAL'
            : 'PATIENT'
        );
        statusControl.setValue('HOLD/RX');
        this.appendFrameActivity('HOLD / RX');
        this.orderFormGroup.get('products.0.frame.activityStatus')?.setValue(
          'HOLD / RX'
        );
        this.ensureHoldFollowUpDefault();
        if (this.orderFormGroup.get('rxStatus')?.value === 'CALL FOR RX') {
          this.orderFormGroup.get('rxStatus')?.setValue('');
        }
        this.setExclusiveJobAction('HOLD / RX');
        statusControl.markAsDirty();
        break;
      case 'HOLD':
        todoControl.setValue('HOLD');
        statusControl.setValue('HOLD');
        this.appendFrameActivity('HOLD');
        this.orderFormGroup.get('products.0.frame.activityStatus')?.setValue(
          'HOLD'
        );
        this.setExclusiveJobAction('HOLD');
        this.ensureActivityEntry('HOLD');
        statusControl.markAsDirty();
        break;
      case 'CALL / RX':
      case 'CALL FOR RX':
        todoControl.setValue('CALL');
        frameSourceControl.setValue(
          frameSourceControl.value === 'PATIENT APPROVAL'
            ? 'PATIENT APPROVAL'
            : 'PATIENT'
        );
        this.orderFormGroup.get('rxStatus')?.setValue('CALL FOR RX');
        this.setExclusiveJobAction('CALL FOR RX');
        statusControl.setValue('CALL FOR RX');
        this.ensureCallFollowUpDefault();
        this.appendFrameActivity('CALL FOR RX');
        this.orderFormGroup.get('products.0.frame.activityStatus')?.setValue(
          'CALL FOR RX'
        );
        statusControl.markAsDirty();
        break;
      case 'CALLED FOR RX': {
        this.appendFrameActivity('CALLED FOR RX', '', { forceNew: true });
        this.ensureCallFollowUpDefault();
        const waiting = this.isHoldRxJob() ? 'HOLD / RX' : 'CALL FOR RX';
        this.orderFormGroup.get('products.0.frame.activityStatus')?.setValue(
          waiting
        );
        break;
      }
      case 'RX RECEIVED': {
        this.appendFrameActivity('RX RECEIVED');
        this.removeJobActions(['CALL FOR RX', 'HOLD / RX']);
        this.completeOpenActivityTasks(['CALL FOR RX', 'CALL / RX', 'HOLD / RX']);
        this.orderFormGroup.get('rxStatus')?.setValue('');
        const currentStatus = String(statusControl.value || '').trim();
        const physical = String(
          this.orderFormGroup.get('products.0.frame.physicalStatus')?.value || ''
        );
        const alreadyRouted =
          /^AWAITING /i.test(currentStatus) ||
          currentStatus === 'RECEIVED' ||
          currentStatus === 'DISPENSED' ||
          currentStatus === 'TO ORDER' ||
          currentStatus === 'TO REPAIR' ||
          /^ORDER /.test(currentStatus) ||
          physical === 'ORDERED' ||
          physical === 'ON-APPROVAL';
        if (!alreadyRouted) {
          todoControl.setValue('ORDER');
          if (this.isEnclosedFrame()) {
            statusControl.setValue(this.getStackOrderStatusForType(orderType));
          } else {
            this.orderFormGroup.get('date')?.setValue(new Date());
            statusControl.setValue('TO ORDER');
            this.appendFrameActivity('TO ORDER');
            this.orderFormGroup.get('products.0.frame.activityStatus')?.setValue(
              'TO ORDER'
            );
          }
          if (
            !this.hasJob('SEND TO STACK') &&
            !this.hasJob('QUOTE') &&
            !this.hasJob('REPAIR') &&
            !this.hasJob('CONVERT TO ORDER')
          ) {
            this.setExclusiveJobAction('SEND TO STACK');
          }
          statusControl.markAsDirty();
        }
        break;
      }
      case 'INQUIRY':
        todoControl.setValue('INQUIRY');
        statusControl.setValue('INQUIRY');
        break;
      case 'SAVE/QUOTE':
        todoControl.setValue('QUOTE');
        statusControl.setValue('QUOTE');
        this.setExclusiveJobAction('QUOTE');
        this.clearQuoteInvoice();
        if (!this.orderFormGroup.get('date')?.value) {
          this.orderFormGroup.get('date')?.setValue(new Date());
        }
        this.ensureActivityEntry('QUOTE CREATED');
        statusControl.markAsDirty();
        break;
      case 'SAVE':
        if (!/^AWAITING /i.test(String(statusControl.value || ''))) {
          todoControl.setValue('SAVE');
        }
        break;
      default:
        todoControl.setValue(destination);
        if (destination !== 'SAVE') {
          statusControl.setValue(
            this.getOrderStatusString(orderType, destination)
          );
        }
        break;
    }
  }

  private ensureActivityEntry(item: string, notes = ''): void {
    const logs = this.orderFormGroup?.get('log')?.value;
    if (Array.isArray(logs) && logs.some((entry) => entry?.item === item)) {
      return;
    }
    const logItem = new LogElement();
    logItem.initials =
      String(this.orderFormGroup.get('initials')?.value || '').trim() ||
      this.getLoggedInInitials();
    logItem.user = this.auth.currentUser._id;
    logItem.item = item;
    logItem.notes = notes;
    logItem.date = Date.now().toString();
    this.prependLogEntry(this.orderFormGroup, logItem);
  }

  private syncFrameOnlyLog(): void {
    const frameOnly = !!this.orderFormGroup.getRawValue()?.frameOnly;
    const logs = this.orderFormGroup?.get('log')?.value;
    const hadFrameOnly =
      Array.isArray(logs) && logs.some((entry) => entry?.item === 'FRAME ONLY');
    if (frameOnly) {
      this.ensureActivityEntry('FRAME ONLY', 'TAXABLE FRAME SALE');
      return;
    }
    if (hadFrameOnly) {
      this.ensureActivityEntry('FRAME ONLY DESELECTED');
    }
  }

  private isHoldRxJob(): boolean {
    const status = String(this.orderFormGroup.get('status')?.value || '').trim();
    const todo = String(this.orderFormGroup.get('todo')?.value || '').trim();
    return this.hasJob('HOLD / RX') || status === 'HOLD/RX' || status === 'HOLD / RX';
  }

  private isQuoteJob(): boolean {
    const status = String(this.orderFormGroup.get('status')?.value || '').trim();
    const todo = String(this.orderFormGroup.get('todo')?.value || '').trim();
    return (
      this.hasJob('QUOTE') ||
      this.hasJob('CONVERT TO ORDER') ||
      status === 'QUOTE' ||
      todo === 'QUOTE'
    );
  }

  private routingDestinationFromPhysicalStatus(): string {
    const physical = String(
      this.orderFormGroup.get('products.0.frame.physicalStatus')?.value || ''
    ).trim();
    if (physical === 'ORDERED') {
      return 'ORDERED FRAME';
    }
    if (physical === 'ON-APPROVAL') {
      return 'ORDERED ON APPROVAL';
    }
    return 'SEND TO STACK';
  }

  private clearQuoteInvoice(): void {
    const control = this.orderFormGroup.get('invoiceNumber');
    control?.clearValidators();
    control?.setValue('');
    control?.updateValueAndValidity();
  }

  private convertQuoteToOrder(onComplete: () => void): void {
    this.orderService.reserveInvoiceNumber().subscribe({
      next: (invoiceNumber) => {
        const control = this.orderFormGroup.get('invoiceNumber');
        control?.setValidators([Validators.required]);
        control?.setValue(invoiceNumber);
        control?.updateValueAndValidity();
        this.ensureActivityEntry(
          'CONVERTED TO ORDER',
          `INVOICE #${invoiceNumber} ASSIGNED`
        );
        if (this.hasJob('CONVERT TO ORDER') || this.hasJob('QUOTE')) {
          this.setExclusiveJobAction('SEND TO STACK');
        }
        onComplete();
      },
      error: () => {
        this.notificationService.show(
          'error',
          'Could not assign invoice number.',
          'OK'
        );
      },
    });
  }

  private ensureHoldFollowUpDefault(): void {
    const followUp = this.orderFormGroup.get('products.0.frame.followUpDate');
    if (!followUp) {
      return;
    }
    const current = followUp.value;
    if (current) {
      const parsed = current instanceof Date ? current : new Date(current);
      if (!isNaN(parsed.getTime())) {
        return;
      }
    }
    followUp.setValue(new Date(Date.now() + 48 * 60 * 60 * 1000));
  }

  private ensureCallFollowUpDefault(): void {
    const followUp = this.orderFormGroup.get('products.0.frame.followUpDate');
    if (!followUp) {
      return;
    }
    const current = followUp.value;
    if (current) {
      const parsed = current instanceof Date ? current : new Date(current);
      if (!isNaN(parsed.getTime())) {
        return;
      }
    }
    followUp.setValue(new Date());
  }

  private followUpDayStart(value: any): Date | null {
    if (!value) {
      return null;
    }
    const parsed = value instanceof Date ? value : new Date(value);
    if (isNaN(parsed.getTime())) {
      return null;
    }
    parsed.setHours(0, 0, 0, 0);
    return parsed;
  }

  private isFollowUpReached(): boolean {
    const start = this.followUpDayStart(
      this.orderFormGroup.get('products.0.frame.followUpDate')?.value
    );
    if (!start) {
      return this.hasJob('CALL FOR RX');
    }
    return start.getTime() <= Date.now();
  }

  private hasFutureFollowUp(): boolean {
    const start = this.followUpDayStart(
      this.orderFormGroup.get('products.0.frame.followUpDate')?.value
    );
    if (!start) {
      return false;
    }
    const tomorrow = new Date();
    tomorrow.setHours(0, 0, 0, 0);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return start.getTime() >= tomorrow.getTime();
  }

  private resolveCallHoldDestination(destination: string): string {
    const isHold = destination === 'SAVE/HOLD';
    const isCall = destination === 'CALL / RX' || destination === 'CALL FOR RX';
    if (!isHold && !isCall) {
      return destination;
    }
    if (this.isFollowUpReached()) {
      this.ensureCallFollowUpDefault();
      return 'CALL FOR RX';
    }
    if (isCall && this.hasFutureFollowUp()) {
      return 'SAVE/HOLD';
    }
    return isCall ? 'CALL FOR RX' : destination;
  }

  private confirmHoldFollowUp(onComplete: (confirmed: boolean) => void): void {
    this.ensureHoldFollowUpDefault();
    const dialogRef = this.dialog.open(HoldFollowUpDialogComponent, {
      width: '420px',
      data: {
        followUpDate: this.orderFormGroup.get('products.0.frame.followUpDate')?.value,
      },
    });
    dialogRef.afterClosed().subscribe((date: Date | null) => {
      if (!date) {
        onComplete(false);
        return;
      }
      this.orderFormGroup.get('products.0.frame.followUpDate')?.setValue(date);
      onComplete(true);
    });
  }

  private applyVoidInvoiceSuffix(): void {
    const control = this.orderFormGroup.get('invoiceNumber');
    const current = String(control?.value || '').trim();
    if (!current || /-V\d*$/i.test(current)) {
      return;
    }
    control.setValue(`${current}-V`);
  }

  private releaseHeldFrameInventory(onComplete: () => void): void {
    const isPof = !!this.orderFormGroup.get('products.0.frame.pof')?.value;
    const enclosed = !!this.orderFormGroup.get('products.0.frame.enclosed')?.value
      || this.orderFormGroup.get('products.0.frame.physicalStatus')?.value === 'ENCLOSED';
    const product = this.orderFormGroup.get('products.0.product')?.value;
    const quantity =
      parseInt(this.orderFormGroup.get('products.0.quantity')?.value, 10) || 1;
    if (isPof || !enclosed || !product?._id) {
      onComplete();
      return;
    }
    this.productService
      .incrementProductInventoryQuantity(product, Math.abs(quantity))
      .subscribe({
        next: () => {
          this.orderFormGroup.get('products.0.frame.enclosed')?.setValue(false);
          onComplete();
        },
        error: () => {
          this.notificationService.show(
            'error',
            'COULD NOT RETURN FRAME TO INVENTORY',
            'OK'
          );
          onComplete();
        },
      });
  }

  private isEnclosedFrame(): boolean {
    const physical = this.orderFormGroup.get('products.0.frame.physicalStatus')?.value;
    const preOrder = this.orderFormGroup.get('products.0.frame.preOrderStatus')?.value;
    return physical === 'ENCLOSED' || preOrder === 'ENCLOSED';
  }

  private isStackToOrderStage(): boolean {
    if (this.isEnclosedFrame()) {
      return false;
    }
    const status = String(this.orderFormGroup.get('status')?.value || '').trim();
    if (status === 'TO ORDER' || status === 'ORDER FRAME') {
      return true;
    }
    const activity = this.orderFormGroup.get('products.0.frame.activity')?.value;
    if (!Array.isArray(activity) || !activity.length) {
      return false;
    }
    const latest = activity[activity.length - 1];
    return latest?.status === 'TO ORDER';
  }

  private applyOnApprovalReceivedFromJob(): void {
    const frame = this.orderFormGroup.get('products.0.frame');
    const physical = String(frame?.get('physicalStatus')?.value || '');
    const frameSource = String(frame?.get('frameSource')?.value || '');
    const todo = String(this.orderFormGroup.get('todo')?.value || '');
    const isOnApproval =
      physical === 'ON-APPROVAL' ||
      frameSource === 'PATIENT APPROVAL' ||
      todo === 'ORDERED ON APPROVAL';
    if (!isOnApproval) {
      return;
    }
    const receivedRaw = frame?.get('dateReceived')?.value
      ? new Date(frame.get('dateReceived').value)
      : new Date();
    const received = isNaN(receivedRaw.getTime()) ? new Date() : receivedRaw;
    frame?.get('dateReceived')?.setValue(received);
    const followUp = new Date(received);
    followUp.setDate(followUp.getDate() + 5);
    frame?.get('followUpDate')?.setValue(followUp);
    this.orderFormGroup.get('todo')?.setValue('ORDER');
    this.orderFormGroup
      .get('status')
      ?.setValue(
        this.getStackOrderStatusForType(this.orderFormGroup.get('type')?.value)
      );
  }

  private appendLogEvent(item: string, notes = ''): void {
    const logs = this.orderFormGroup?.get('log')?.value;
    const latest = Array.isArray(logs) && logs.length ? logs[0] : null;
    if (latest && String(latest.item || '') === item) {
      return;
    }
    const logItem = new LogElement();
    logItem.initials =
      String(this.orderFormGroup.get('initials')?.value || '').trim() ||
      this.getLoggedInInitials();
    logItem.user = this.auth.currentUser._id;
    logItem.item = item;
    logItem.notes = notes;
    logItem.date = Date.now().toString();
    this.prependLogEntry(this.orderFormGroup, logItem);
  }

  private appendFrameActivity(
    status: string,
    referenceNumber = '',
    extra: {
      carrier?: string;
      trackingNumber?: string;
      labVendor?: string;
      eta?: any;
      forceNew?: boolean;
      keepActivityStatus?: boolean;
    } = {}
  ): void {
    let activity = this.orderFormGroup.get('products.0.frame.activity') as FormArray;
    if (!activity) {
      const frame = this.orderFormGroup.get('products.0.frame') as FormGroup;
      if (!frame) {
        return;
      }
      activity = this.fb.array([]);
      frame.addControl('activity', activity);
    }
    const last = activity.length
      ? activity.at(activity.length - 1).value
      : null;
    const recentMatch = activity.controls
      .map((control, index) => ({ index, value: control.value }))
      .slice(-3)
      .reverse()
      .find((row) => String(row.value?.status || '') === status);
    if (recentMatch && (last?.status === status || !extra.forceNew || recentMatch.value?.kind)) {
      const existing = recentMatch.value;
      const patch: Record<string, any> = {};
      if (referenceNumber && !existing.referenceNumber) {
        patch.referenceNumber = referenceNumber;
      }
      if (extra.carrier && !existing.carrier) {
        patch.carrier = extra.carrier;
      }
      if (extra.trackingNumber && !existing.trackingNumber) {
        patch.trackingNumber = extra.trackingNumber;
      }
      if (extra.labVendor && !existing.labVendor) {
        patch.labVendor = extra.labVendor;
      }
      if (extra.eta && !existing.eta) {
        patch.eta = extra.eta;
      }
      if (Object.keys(patch).length) {
        activity.at(recentMatch.index).patchValue(patch);
      }
      return;
    }
    const initials =
      String(this.orderFormGroup.get('initials')?.value || '').trim() ||
      this.getLoggedInInitials();
    const isTask = isOpenTaskStatus(status);
    const isAwaiting = /^AWAITING /i.test(status);
    activity.push(
      this.fb.group({
        id: '',
        kind: isTask ? 'open' : isAwaiting ? 'awaiting' : 'completed',
        date: new Date(),
        status,
        branch: '',
        notes: '',
        repairItems: [[]],
        waitingOn: '',
        vendor: '',
        followUpDate: '',
        followUpNumber: 0,
        activityChoice: '',
        reopenedFrom: '',
        sourceId: '',
        description: '',
        referenceNumber: referenceNumber || '',
        carrier: extra.carrier || '',
        trackingNumber: extra.trackingNumber || '',
        labVendor: extra.labVendor || '',
        eta: extra.eta || this.orderFormGroup.get('eta')?.value || '',
        completed: !isTask && !isAwaiting,
        completedBy: isTask || isAwaiting ? '' : initials,
        completedAt: isTask || isAwaiting ? '' : new Date(),
      })
    );
    if (!extra.keepActivityStatus) {
      this.orderFormGroup.get('products.0.frame.activityStatus')?.setValue(status);
    }
    const notes = [
      extra.labVendor,
      extra.carrier,
      extra.trackingNumber ? `TRACKING #${extra.trackingNumber}` : '',
      referenceNumber ? `REF. #${referenceNumber}` : '',
    ]
      .filter(Boolean)
      .join(' — ');
    this.appendLogEvent(status, notes);
  }

  private shouldDeductEnclosedInventory(): boolean {
    const physicalStatus = this.orderFormGroup.get(
      'products.0.frame.physicalStatus'
    )?.value;
    const enclosed = !!this.orderFormGroup.get('products.0.frame.enclosed')?.value;
    const isPof = !!this.orderFormGroup.get('products.0.frame.pof')?.value;
    const product = this.orderFormGroup.get('products.0.product')?.value;
    return physicalStatus === 'ENCLOSED' && !enclosed && !isPof && !!product?._id;
  }

  private deductEnclosedInventory(onComplete: () => void): void {
    const product = this.orderFormGroup.get('products.0.product')?.value;
    const quantity =
      parseInt(this.orderFormGroup.get('products.0.quantity')?.value, 10) || 1;
    this.productService
      .incrementProductInventoryQuantity(product, -Math.abs(quantity))
      .subscribe({
        next: () => {
          this.orderFormGroup.get('products.0.frame.enclosed')?.setValue(true);
          this.orderFormGroup.get('products.0.frame.preOrderStatus')?.setValue(
            'ENCLOSED'
          );
          onComplete();
        },
        error: () => {
          this.notificationService.show(
            'error',
            'COULD NOT DEDUCT INVENTORY',
            'OK'
          );
        },
      });
  }

  private addToCart(activityStatus = 'DISPENSED'): void {
    const newOrder = this.orderFormGroup.get('products.0._id').value == '';
    const product = this.orderFormGroup.get('products.0.product')?.value;
    const quantity =
      parseInt(this.orderFormGroup.get('products.0.quantity')?.value, 10) || 1;
    const isPof = !!this.orderFormGroup.get('products.0.frame.pof')?.value;
    const holdRx = this.isHoldRxJob();

    if (holdRx) {
      this.orderFormGroup.get('todo')?.setValue('HOLD');
      this.orderFormGroup.get('status')?.setValue('HOLD/RX');
      this.syncFrameOnlyLog();
      this.ensureNewBusinessDate();
      const persistHold = () => this.persistOrder(newOrder, 'SAVE/HOLD');
      if (!isPof && product?._id && !this.orderFormGroup.get('products.0.frame.enclosed')?.value) {
        this.productService
          .incrementProductInventoryQuantity(product, -Math.abs(quantity))
          .subscribe({
            next: () => {
              this.orderFormGroup.get('products.0.frame.enclosed')?.setValue(true);
              persistHold();
            },
            error: () => {
              this.notificationService.show(
                'error',
                'COULD NOT DEDUCT INVENTORY',
                'OK'
              );
            },
          });
        return;
      }
      persistHold();
      return;
    }

    if (activityStatus === 'DISPENSED' && !this.canDispense()) {
      this.ensureNewBusinessDate();
      this.notificationService.show(
        'error',
        'Balance due must be $0.00 before the job can be dispensed',
        'OK'
      );
      this.persistOrder(newOrder, 'SAVE');
      return;
    }

    this.orderFormGroup.get('todo').setValue('ADD TO CART');
    this.orderFormGroup.get('status').setValue('DISPENSED');
    this.orderFormGroup.get('dispensedDate')?.setValue(new Date());
    this.ensureNewBusinessDate();
    this.orderFormGroup.get('products.0.frame.frameSource').setValue(
      isPof ? 'PATIENT' : this.orderFormGroup.get('products.0.frame.frameSource')?.value || 'PATIENT'
    );
    if (activityStatus === 'SHIPPED') {
      const shipDate = new Date();
      this.orderFormGroup.get('products.0.frame.shipDate')?.setValue(shipDate);
      this.appendFrameActivity(
        'SHIPPED',
        String(this.orderFormGroup.get('products.0.frame.referenceNumber')?.value || '').trim(),
        {
          carrier: String(
            this.orderFormGroup.get('products.0.frame.carrier')?.value || ''
          ).trim(),
          trackingNumber: String(
            this.orderFormGroup.get('products.0.frame.trackingNumber')?.value || ''
          ).trim(),
        }
      );
    } else {
      this.appendFrameActivity('DISPENSED');
    }
    this.syncFrameOnlyLog();

    const persist = () =>
      this.persistOrder(newOrder, activityStatus === 'SHIPPED' ? 'SHIPPED' : 'ADD TO CART');

    if (!isPof && product?._id) {
      this.productService
        .incrementProductInventoryQuantity(product, -Math.abs(quantity))
        .subscribe({
          next: () => persist(),
          error: () => {
            this.notificationService.show(
              'error',
              'COULD NOT DEDUCT INVENTORY',
              'OK'
            );
          },
        });
      return;
    }

    persist();
  }

  private voidOrder(): void {
    const order: Order = this.orderFormGroup.value;
    const newOrder = this.orderFormGroup.get('products.0._id').value == '';
    const clearTaxAndCancel = () => {
      this.orderFormGroup.get('products.0.tax').setValue(0);
      this.orderFormGroup.get('tax')?.setValue(0);
      this.orderFormGroup.get('todo').setValue('VOID');
      this.orderFormGroup.get('status').setValue('VOID');
      this.applyVoidInvoiceSuffix();
      this.appendFrameActivity('VOID');
    };

    const persistVoid = () => {
      this.releaseHeldFrameInventory(() => {
        this.persistOrder(newOrder, 'VOID');
      });
    };

    if (!this.hasPaymentOccurred(order)) {
      clearTaxAndCancel();
      persistVoid();
      return;
    }

    const dialogRef = this.dialog.open(ReturnOrderDialogComponent, {
      width: '250px',
      data: { message: 'VOID ORDER? ENTER STORE CREDIT', order, creditOnly: true },
    });

    dialogRef
      .afterClosed()
      .pipe(takeWhile((result) => !!result))
      .subscribe((result: ReturnOrderDialogResult) => {
        let credit =
          result?.netCredit ??
          result?.credit ??
          dialogRef.componentInstance.buildResult().netCredit;
        if (typeof credit === 'string') {
          credit = parseFloat(String(credit).replace(/,/g, ''));
        }
        if (isNaN(credit as number)) {
          credit = 0;
        }

        clearTaxAndCancel();

        const logItem = new LogElement();
        logItem.initials =
          order['initials'] || this.auth.currentUser.initials;
        logItem.user = this.auth.currentUser._id;
        logItem.item = 'VOIDED';
        logItem.notes = credit ? `STORE CREDIT: ${credit}` : '';
        logItem.date = Date.now().toString();
        const logControl = this.orderFormGroup.get('log');
        const currentLog = Array.isArray(logControl?.value) ? logControl.value : [];
        logControl?.setValue([logItem, ...currentLog]);

        this.releaseHeldFrameInventory(() => {
          this.saveFrame(this.orderFormGroup, newOrder).subscribe(() => {
          const saveMessage = this.getSaveSuccessMessage('VOID');
          const finish = () => this.navigateBack({ saveMessage });
          if (credit && this.patientId) {
            this.paymentService
              .issueStoreCredit({
                patientId: this.patientId,
                amount: credit,
                sourceInvoice: order.invoiceNumber,
                sourceOrder: order._id,
                type: 'Return Credit',
                initials: logItem.initials,
                user: this.auth.currentUser._id,
              })
              .subscribe({
                next: () => finish(),
                error: () => finish(),
              });
          } else {
            finish();
          }
          });
        });
      });
  }

  onFinancialOrderUpdated(order: Order): void {
    if (!order || !this.orderFormGroup) {
      return;
    }
    this.orderFormGroup.get('financials')?.setValue(order.financials, { emitEvent: false });
    this.orderFormGroup.get('lineItems')?.setValue(order.lineItems || [], { emitEvent: false });
    this.orderFormGroup
      .get('financialEvents')
      ?.setValue(order.financialEvents || [], { emitEvent: false });
    this.orderFormGroup.get('acknowledgedAt')?.setValue(order.acknowledgedAt || null, {
      emitEvent: false,
    });
    this.orderFormGroup.get('paidInFullDate')?.setValue(order.paidInFullDate || null, {
      emitEvent: false,
    });
    if (order.payerName) {
      this.orderFormGroup.get('payerName')?.setValue(order.payerName, { emitEvent: false });
    }
  }

  private canDispense(): boolean {
    const financials = this.orderFormGroup?.get('financials')?.value;
    if (financials && financials.orderTotal != null) {
      return FinancialCalculator.money(financials.balanceDue) <= 0.009;
    }
    return true;
  }

  private ensureNewBusinessDate(): void {
    const status = String(this.orderFormGroup?.get('status')?.value || '');
    const todo = String(this.orderFormGroup?.get('todo')?.value || '');
    if (status === 'QUOTE' || todo === 'QUOTE') {
      return;
    }
    if (!this.orderFormGroup?.get('newBusinessDate')?.value) {
      this.orderFormGroup.get('newBusinessDate')?.setValue(new Date());
    }
  }

  private hasPaymentOccurred(order: Order): boolean {
    const paid = FinancialCalculator.money(order?.financials?.paid);
    if (paid > 0) {
      return true;
    }
    if (order?.paidInFullDate) {
      return true;
    }
    if (order?.status === 'DISPENSED') {
      return true;
    }
    const tax = parseFloat(
      String(order?.products?.[0]?.tax ?? order?.tax ?? 0).replace(/,/g, '')
    );
    if (!isNaN(tax) && tax > 0 && order?.status && order.status !== 'QUOTE') {
      return true;
    }
    const log = Array.isArray(order?.log) ? order.log : [];
    return log.some((entry) =>
      /PAID|PAYMENT|ADD TO CART|DEPOSIT|DISPENSED|SHIPPED/i.test(String(entry?.item || ''))
    );
  }

  persistOrder(newOrder: boolean, destination: string): void {
    const announceNewOrder = this.isNewOrder || newOrder;
    this.syncEtaOntoOrderedActivity();
    if (destination !== 'SAVE/QUOTE' && destination !== 'VOID' && destination !== 'DELETE') {
      this.ensureNewBusinessDate();
    }

    // Re-apply right before save so Frame Status mat-select cannot keep AWAITING *
    if (destination === 'SEND TO STACK') {
      const status = this.isEnclosedFrame()
        ? this.getStackOrderStatusForType(this.orderFormGroup.get('type')?.value)
        : 'TO ORDER';
      this.orderFormGroup.get('status').setValue(status, { emitEvent: false });
      this.orderFormGroup.get('todo').setValue('ORDER', { emitEvent: false });
    } else if (destination === 'SAVE/HOLD') {
      this.orderFormGroup.get('status').setValue('HOLD/RX', { emitEvent: false });
      this.orderFormGroup.get('todo').setValue('HOLD', { emitEvent: false });
      this.setExclusiveJobAction('HOLD / RX', false);
    } else if (destination === 'HOLD') {
      this.orderFormGroup.get('status').setValue('HOLD', { emitEvent: false });
      this.orderFormGroup.get('todo').setValue('HOLD', { emitEvent: false });
      this.setExclusiveJobAction('HOLD', false);
    } else if (destination === 'SAVE/QUOTE') {
      this.orderFormGroup.get('status').setValue('QUOTE', { emitEvent: false });
      this.orderFormGroup.get('todo').setValue('QUOTE', { emitEvent: false });
      this.setExclusiveJobAction('QUOTE', false);
    } else if (destination === 'CALL FOR RX' || destination === 'CALL / RX') {
      this.orderFormGroup.get('status').setValue('CALL FOR RX', { emitEvent: false });
      this.orderFormGroup.get('todo').setValue('CALL', { emitEvent: false });
      this.setExclusiveJobAction('CALL FOR RX', false);
      this.orderFormGroup.get('rxStatus')?.setValue('CALL FOR RX', { emitEvent: false });
    } else if (destination === 'SENT FOR REPAIR') {
      this.orderFormGroup.get('status').setValue('AWAITING REPAIR', { emitEvent: false });
      this.orderFormGroup.get('todo').setValue('REPAIR', { emitEvent: false });
    } else if (destination === 'TO REPAIR') {
      this.orderFormGroup.get('status').setValue('TO REPAIR', { emitEvent: false });
      this.orderFormGroup.get('todo').setValue('REPAIR', { emitEvent: false });
      this.setExclusiveJobAction('REPAIR', false);
    } else if (destination === 'SHIPPED' || destination === 'ADD TO CART') {
      this.orderFormGroup.get('status').setValue('DISPENSED', { emitEvent: false });
      this.orderFormGroup.get('todo').setValue('ADD TO CART', { emitEvent: false });
    }

    const save$ =
      destination === 'RECEIVED FRAME'
        ? this.ensureReceivedFrameCatalog().pipe(
            switchMap(() =>
              this.saveFrameWithDestination(
                this.orderFormGroup,
                newOrder,
                destination
              )
            )
          )
        : this.saveFrameWithDestination(
            this.orderFormGroup,
            newOrder,
            destination
          );

    save$.subscribe(() => {
      this.isNewOrder = false;
      if (this.embedded) {
        this.orderChangedInEmbed = true;
      }
      const saveMessage = this.getSaveSuccessMessage(destination);
      if (destination === 'SAVE') {
        this.notificationService.show('success', saveMessage, 'OK');
        this.autofillInitials();
        this.cdRef.detectChanges();
        return;
      }
      this.navigateBack({
        orderAdded: announceNewOrder,
        saveMessage,
      });
    });
  }

  getSaveSuccessMessage(destination: string): string {
    switch (destination) {
      case 'SEND TO STACK':
        return 'Order successfully sent to stack';
      case 'ORDERED FRAME':
      case 'ORDERED':
        return this.awaitingSaveMessage('Order successfully sent to awaiting frames');
      case 'ORDERED LENSES':
        return this.awaitingSaveMessage('Order successfully sent to awaiting lenses');
      case 'RECEIVED LENSES':
        return this.receivedLensesSaveMessage();
      case 'RECEIVED FRAME':
        return this.receivedFrameSaveMessage();
      case 'ORDERED ON APPROVAL':
        return 'Order successfully ordered on approval';
      case 'SENT FOR REPAIR':
        return 'Order sent for repair';
      case 'TO REPAIR':
      case 'REPAIR':
        return 'Repair job sent to TO REPAIR';
      case 'SAVE/HOLD':
        return 'Order successfully saved to hold/RX';
      case 'HOLD':
        return 'Order successfully saved to HOLD';
      case 'SAVE/QUOTE':
        return 'Order successfully saved as quote';
      case 'CONVERT TO ORDER':
        return 'Quote successfully converted to order';
      case 'CALL / RX':
      case 'CALL FOR RX':
        return 'Order successfully sent to call for RX';
      case 'CALLED FOR RX':
        return 'Call for RX recorded';
      case 'RX RECEIVED':
        return 'RX received';
      case 'INQUIRY':
        return 'Order successfully saved as inquiry';
      case 'ADD TO CART':
        return 'Order successfully added to cart';
      case 'SHIPPED':
        return 'Order marked shipped and dispensed';
      case 'NEW RX':
        return 'New RX job created; original invoice unchanged';
      case 'RE-DO':
        return 'Re-do job created; original invoice unchanged';
      case 'VOID':
        return 'Order successfully voided';
      case 'SAVE':
        return 'Order successfully saved';
      case 'RETURN':
        return 'Return processed; original invoice unchanged';
      case 'EXCHANGE':
        return 'Exchange job created; original invoice unchanged';
      case 'DELETE':
        return 'Order deleted';
      case 'RE-STYLE':
        return 'Order re-styled; new job created';
      case 'DUPLICATE':
        return 'Duplicate job created with current pricing';
      case 'ADDED TO INVENTORY':
        return 'Order successfully added to inventory';
      default:
        return 'Order successfully saved';
    }
  }

  private awaitingSaveMessage(fallback: string): string {
    const status = String(this.orderFormGroup?.get('status')?.value || '').trim();
    if (status === 'AWAITING FRAME + LENSES') {
      return 'Order successfully sent to awaiting frame + lenses';
    }
    if (status === 'AWAITING LENSES') {
      return 'Order successfully sent to awaiting lenses';
    }
    if (status === 'AWAITING FRAME' || status === 'AWAITING PART' || status === 'AWAITING ACCESSORY') {
      return 'Order successfully sent to awaiting frames';
    }
    return fallback;
  }

  private receivedLensesSaveMessage(): string {
    const status = String(this.orderFormGroup?.get('status')?.value || '').trim();
    if (
      status === 'AWAITING FRAME' ||
      status === 'AWAITING PART' ||
      status === 'AWAITING ACCESSORY'
    ) {
      return 'Lenses marked received; job moved to awaiting frames';
    }
    return 'Lenses marked received';
  }

  private receivedFrameSaveMessage(): string {
    const status = String(this.orderFormGroup?.get('status')?.value || '').trim();
    if (status === 'AWAITING LENSES') {
      return 'Frame marked received; job moved to awaiting lenses';
    }
    return 'Order successfully received';
  }

  /**
   * Saves the order and forces stack/awaiting status onto the POST body so the
   * Frame Status dropdown cannot override destination metadata.
   */
  private saveFrameWithDestination(
    order: FormGroup,
    newOrder: boolean,
    destination: string
  ) {
    this.fieldOpen = true;

    if (!order.controls.notes.dirty) {
      order.controls.notes.setValue('');
    }
    if (order.value._id == 'NEW') {
      order.controls._id.setValue(null);
    }
    for (let x = 0; x < order.value.products.length; x++) {
      if (order.value.products[x]._id == 'NEW') {
        order.get(`products.${x}._id`).setValue(null);
      }
      let cost = order.get(`products.${x}.cost`);
      let retail = order.get(`products.${x}.retail`);
      if (typeof cost.value === 'string') {
        cost.setValue(parseFloat(cost.value.replace(/,/g, '')));
      }
      if (typeof retail.value === 'string') {
        retail.setValue(parseFloat(retail.value.replace(/,/g, '')));
      }
    }
    this.productCosts[order.value._id] = order.get('products.0.cost').value;

    this.ensureInitialsFilled();

    if (!newOrder) {
      this.addDirtyValuesToLog(order);
    }

    const payload = OrderForm.sanitizeOrderPayload(order.getRawValue());
    if (destination === 'SEND TO STACK') {
      payload.status = this.isEnclosedFrame()
        ? this.getStackOrderStatusForType(payload.type)
        : 'TO ORDER';
      payload.todo = 'ORDER';
      if (payload.rxStatus === 'CALL FOR RX') {
        payload.rxStatus = '';
      }
    } else if (destination === 'SAVE/HOLD') {
      payload.status = 'HOLD/RX';
      payload.todo = 'HOLD';
      payload.jobAction = 'HOLD / RX';
      payload.jobActions = withExclusiveJobAction(
        jobActionList(payload.jobAction, payload.jobActions),
        'HOLD / RX'
      );
      if (payload.rxStatus === 'CALL FOR RX') {
        payload.rxStatus = '';
      }
    } else if (destination === 'HOLD') {
      payload.status = 'HOLD';
      payload.todo = 'HOLD';
      payload.jobAction = 'HOLD';
      payload.jobActions = withExclusiveJobAction(
        jobActionList(payload.jobAction, payload.jobActions),
        'HOLD'
      );
    } else if (destination === 'SAVE/QUOTE') {
      payload.status = 'QUOTE';
      payload.todo = 'QUOTE';
      payload.jobAction = 'QUOTE';
      payload.jobActions = withExclusiveJobAction(
        jobActionList(payload.jobAction, payload.jobActions),
        'QUOTE'
      );
      payload.invoiceNumber = '';
    } else if (destination === 'CALL / RX' || destination === 'CALL FOR RX') {
      payload.status = 'CALL FOR RX';
      payload.todo = 'CALL';
      payload.jobAction = 'CALL FOR RX';
      payload.jobActions = withExclusiveJobAction(
        jobActionList(payload.jobAction, payload.jobActions),
        'CALL FOR RX'
      );
      payload.rxStatus = 'CALL FOR RX';
    } else if (destination === 'CALLED FOR RX') {
      payload.status = this.orderFormGroup.get('status')?.value;
      payload.todo = this.orderFormGroup.get('todo')?.value;
      payload.rxStatus = this.orderFormGroup.get('rxStatus')?.value;
    } else if (destination === 'RX RECEIVED') {
      payload.status = this.orderFormGroup.get('status')?.value;
      payload.todo = this.orderFormGroup.get('todo')?.value;
      payload.rxStatus = '';
      payload.jobActions = this.currentJobActions;
      payload.jobAction = exclusiveJobAction(this.currentJobActions);
    } else if (destination === 'INQUIRY') {
      payload.status = 'INQUIRY';
      payload.todo = 'INQUIRY';
    } else if (destination === 'SENT FOR REPAIR') {
      payload.status = 'AWAITING REPAIR';
      payload.todo = 'REPAIR';
    } else if (destination === 'TO REPAIR') {
      payload.status = 'TO REPAIR';
      payload.todo = 'REPAIR';
      payload.jobAction = 'REPAIR';
      payload.jobActions = withExclusiveJobAction(
        jobActionList(payload.jobAction, payload.jobActions),
        'REPAIR'
      );
    } else if (destination === 'SHIPPED' || destination === 'ADD TO CART') {
      payload.status = 'DISPENSED';
      payload.todo = 'ADD TO CART';
    } else if (
      destination === 'ORDERED FRAME' ||
      destination === 'ORDERED' ||
      destination === 'ORDERED ON APPROVAL' ||
      destination === 'ORDERED LENSES'
    ) {
      payload.status = this.getAwaitingStatusForType(payload.type);
      if (destination === 'ORDERED LENSES' || this.hasJob(ORDER_LENSES_ACTION)) {
        payload.lensStatus = 'ORDERED';
      }
    } else if (destination === 'RECEIVED LENSES') {
      payload.status = this.orderFormGroup.get('status')?.value;
      payload.lensStatus = 'RECEIVED';
    } else if (destination === 'RECEIVED FRAME') {
      payload.status = this.orderFormGroup.get('status')?.value;
    }

    return this.orderService.updateOrderFull(payload).pipe(
      map((order2) => {
        this.finalizeOrderSave(order, order2);
        return true;
      })
    );
  }

  getOrderStatusString(type, destination): string {
    return /^ORDERED/.test(destination)
      ? this.getAwaitingStatusForType(type)
      : this.getStackOrderStatusForType(type);
  }

  /** Status for Stack To-Do / Hold — excluded from Awaiting Frames. */
  getStackOrderStatusForType(type: string): string {
    switch (type) {
      case 'A':
        return 'ORDER ACCESSORY';
      case 'F':
        return 'ORDER PART';
      case 'O':
      case 'N':
      default:
        return 'ORDER FRAME';
    }
  }

  /** Status set when frame is ordered from the Stack — appears in Awaiting Frames. */
  getAwaitingStatusForType(type: string): string {
    if (type === 'A' || type === 'F') {
      return type === 'A' ? 'AWAITING ACCESSORY' : 'AWAITING PART';
    }
    const physical = String(
      this.orderFormGroup.get('products.0.frame.physicalStatus')?.value || ''
    );
    const status = String(this.orderFormGroup.get('status')?.value || '');
    const frameAwaiting =
      physical === 'ORDERED' ||
      physical === 'ON-APPROVAL' ||
      /^AWAITING FRAME/.test(status) ||
      status === 'AWAITING PART' ||
      status === 'AWAITING ACCESSORY' ||
      status === 'AWAITING FRAME + LENSES';
    return combinedAwaitingStatus({
      type,
      status,
      physicalStatus: physical,
      jobAction: this.orderFormGroup.get('jobAction')?.value,
      jobActions: this.currentJobActions,
      lensStatus: this.orderFormGroup.get('lensStatus')?.value,
      frameAwaiting,
    });
  }

  private get currentJobActions(): string[] {
    return jobActionList(
      this.orderFormGroup?.get('jobAction')?.value,
      this.orderFormGroup?.get('jobActions')?.value
    );
  }

  private hasJob(action: string): boolean {
    return hasJobAction(
      this.orderFormGroup?.get('jobAction')?.value,
      this.orderFormGroup?.get('jobActions')?.value,
      action
    );
  }

  private setExclusiveJobAction(action: string, emitEvent = true): void {
    const next = withExclusiveJobAction(this.currentJobActions, action);
    if (!this.orderFormGroup.get('jobActions')) {
      this.orderFormGroup.addControl('jobActions', this.fb.control(next));
    } else {
      this.orderFormGroup.get('jobActions')?.setValue(next, { emitEvent });
    }
    this.orderFormGroup.get('jobAction')?.setValue(action, { emitEvent });
  }

  private removeJobActions(actions: string[]): void {
    let next = this.currentJobActions;
    for (const action of actions) {
      next = toggleJobAction(next, action, false);
    }
    if (!this.orderFormGroup.get('jobActions')) {
      this.orderFormGroup.addControl('jobActions', this.fb.control(next));
    } else {
      this.orderFormGroup.get('jobActions')?.setValue(next);
    }
    this.orderFormGroup
      .get('jobAction')
      ?.setValue(exclusiveJobAction(next) || next[0] || '');
  }

  private completeOpenActivityTasks(statuses: string[]): void {
    const activity = this.orderFormGroup.get(
      'products.0.frame.activity'
    ) as FormArray;
    if (!activity) {
      return;
    }
    const wanted = new Set(statuses.map((status) => String(status).trim()));
    const initials =
      String(this.orderFormGroup.get('initials')?.value || '').trim() ||
      this.getLoggedInInitials();
    const now = new Date();
    const cancelFollowUp = statuses.some(
      (status) =>
        status === 'CALL FOR RX' ||
        status === 'CALL / RX' ||
        status === 'HOLD / RX'
    );
    for (let i = activity.length - 1; i >= 0; i--) {
      const group = activity.at(i) as FormGroup;
      const status = String(group.get('status')?.value || '').trim();
      const kind = String(group.get('kind')?.value || '').trim();
      if (
        cancelFollowUp &&
        (/^FOLLOW UP \/ RX/i.test(status) || status === 'AWAITING RX') &&
        !group.get('completed')?.value
      ) {
        if (status === 'AWAITING RX' || kind === 'awaiting') {
          activity.removeAt(i);
          continue;
        }
        if (/^FOLLOW UP \//i.test(status)) {
          activity.removeAt(i);
          continue;
        }
      }
      if (!wanted.has(status) || group.get('completed')?.value) {
        continue;
      }
      group.patchValue({
        completed: true,
        completedBy: initials,
        completedAt: now,
        kind: 'completed',
      });
    }
    if (cancelFollowUp) {
      this.orderFormGroup.get('products.0.frame.followUpDate')?.setValue('');
    }
  }

  private ensureReceivedFrameCatalog() {
    const frame = this.orderFormGroup.get('products.0.frame')?.value || {};
    const orderType = this.orderFormGroup.get('type')?.value;
    const specialOrder = !!this.orderFormGroup.get('specialOrder')?.value;
    const isPof = !!frame.pof;
    const part = !!this.orderFormGroup.get('part')?.value;
    if (specialOrder || isPof || part || orderType === 'F') {
      return of(null);
    }
    const product = this.orderFormGroup.get('products.0.product')?.value;
    if (!product || (!product.model && !product.vendor)) {
      return of(null);
    }
    const productId = String(product._id || '').trim();
    if (productId && productId !== 'NEW' && productId !== 'null') {
      return of(null);
    }
    const payload = { ...product, specialOrder: false };
    return this.productService.incrementProductInventoryQuantity(payload, 0).pipe(
      map((res) => {
        const created = res?.product;
        if (created?._id) {
          this.orderFormGroup.get('products.0.product._id')?.setValue(created._id);
          this.vendorOrderService
            .addProductIdToVendorOrder(this.orderFormGroup.value)
            .subscribe(() => {});
        }
        return created;
      }),
      catchError(() => of(null))
    );
  }

  private syncEtaOntoOrderedActivity(): void {
    const eta = this.orderFormGroup?.get('eta')?.value;
    const activity = this.orderFormGroup?.get(
      'products.0.frame.activity'
    ) as FormArray;
    if (!activity || !eta) {
      return;
    }
    for (let i = activity.length - 1; i >= 0; i--) {
      const status = String(activity.at(i).get('status')?.value || '');
      if (status === 'ORDERED' || status === 'SENT FOR REPAIR') {
        activity.at(i).get('eta')?.setValue(eta);
        return;
      }
    }
  }

  navigateBack(options?: {
    orderAdded?: boolean;
    saveMessage?: string;
  }): void {
    if (this.embedded) {
      if (options?.saveMessage) {
        this.notificationService.show('success', options.saveMessage, 'OK');
      }
      this.closeEmbed.emit({
        saved: this.orderChangedInEmbed || !!options?.orderAdded,
      });
      return;
    }
    this.router.navigate(['/admin/patient', this.patientId], {
      state: {
        ...(options?.orderAdded ? { orderAdded: true } : {}),
        ...(options?.saveMessage
          ? { orderSaveMessage: options.saveMessage }
          : {}),
      },
    });
  }

  getLoggedInInitials(): string {
    const fromUser = String(this.auth?.currentUser?.initials || '').trim();
    if (fromUser) {
      return fromUser.toUpperCase();
    }
    return String(this.auth?.initials || '').trim().toUpperCase();
  }

  autofillInitials(): void {
    const control = this.orderFormGroup?.get('initials');
    if (!control) {
      return;
    }
    const initials = this.getLoggedInInitials();
    if (initials) {
      control.setValue(initials);
    }
  }

  ensureInitialsFilled(): void {
    const control = this.orderFormGroup?.get('initials');
    if (!control) {
      return;
    }
    if (!String(control.value || '').trim()) {
      this.autofillInitials();
    }
  }

  returnOrder(): void {
    const originalInvoice = String(
      this.orderFormGroup.get('invoiceNumber')?.value || ''
    ).trim();
    const dialogRef = this.dialog.open(ReturnOrderDialogComponent, {
      width: '420px',
      data: {
        message: 'RETURN FRAME?',
        order: this.orderFormGroup.getRawValue(),
        mode: 'return',
      },
    });

    dialogRef
      .afterClosed()
      .pipe(takeWhile((result) => !!result))
      .subscribe((result: ReturnOrderDialogResult) => {
        const dialogResult =
          result?.netCredit !== undefined
            ? result
            : dialogRef.componentInstance.buildResult();
        this.ensureInitialsFilled();
        const originalPayload = this.orderFormGroup.getRawValue();
        this.normalizeOrderMoneyFields(originalPayload);
        const initials =
          String(this.orderFormGroup.get('initials')?.value || '').trim() ||
          this.getLoggedInInitials();

        this.orderService
          .getPatientOrders(this.patientId, 'orders')
          .pipe(
            switchMap((orders: Order[]) =>
              ReturnJob.create$(
                originalPayload,
                originalInvoice,
                orders,
                {
                  credit: dialogResult.credit,
                  restockingFee: dialogResult.restockingFee,
                  netCredit: dialogResult.netCredit,
                  restockingFeeApplied: dialogResult.applyRestockingFee !== false,
                },
                {
                  patientId: this.patientId,
                  userId: this.auth.currentUser._id,
                  store: this.auth.getStore(),
                  initials,
                },
                {
                  orderService: this.orderService,
                  productService: this.productService,
                  patientService: this.patientService,
                }
              )
            )
          )
          .subscribe({
            next: (created: Order) => {
              const newOrderId = created?._id;
              const saveMessage = this.getSaveSuccessMessage('RETURN');

              if (this.embedded) {
                this.orderChangedInEmbed = true;
                this.notificationService.show('success', saveMessage, 'OK');
                this.closeEmbed.emit({ saved: true });
                return;
              }

              this.notificationService.show('success', saveMessage, 'OK');
              if (newOrderId) {
                this.router.navigate([
                  '/admin/patient',
                  this.patientId,
                  'order',
                  newOrderId,
                ]);
                return;
              }
              this.navigateBack({
                orderAdded: true,
                saveMessage,
              });
            },
            error: () => {
              this.notificationService.show(
                'error',
                'Could not create return',
                'OK'
              );
            },
          });
      });
  }

  exchangeOrder(): void {
    const originalInvoice = String(
      this.orderFormGroup.get('invoiceNumber')?.value || ''
    ).trim();
    const dialogRef = this.dialog.open(ReturnOrderDialogComponent, {
      width: '420px',
      data: {
        message: 'EXCHANGE FRAME?',
        order: this.orderFormGroup.getRawValue(),
        mode: 'exchange',
      },
    });

    dialogRef
      .afterClosed()
      .pipe(takeWhile((result) => !!result))
      .subscribe((result: ReturnOrderDialogResult) => {
        const dialogResult =
          result?.netCredit !== undefined
            ? result
            : dialogRef.componentInstance.buildResult();
        this.ensureInitialsFilled();
        const originalPayload = this.orderFormGroup.getRawValue();
        this.normalizeOrderMoneyFields(originalPayload);
        const initials =
          String(this.orderFormGroup.get('initials')?.value || '').trim() ||
          this.getLoggedInInitials();

        this.orderService
          .getPatientOrders(this.patientId, 'orders')
          .pipe(
            switchMap((orders: Order[]) =>
              ExchangeJob.create$(
                originalPayload,
                originalInvoice,
                orders,
                {
                  credit: dialogResult.credit,
                  restockingFee: dialogResult.restockingFee,
                  netCredit: dialogResult.netCredit,
                },
                {
                  patientId: this.patientId,
                  userId: this.auth.currentUser._id,
                  store: this.auth.getStore(),
                  initials,
                },
                {
                  orderService: this.orderService,
                  productService: this.productService,
                  patientService: this.patientService,
                }
              )
            )
          )
          .subscribe({
            next: (created: Order) => {
              const newOrderId = created?._id;
              const saveMessage = this.getSaveSuccessMessage('EXCHANGE');

              if (this.embedded) {
                this.orderChangedInEmbed = true;
                this.notificationService.show('success', saveMessage, 'OK');
                this.closeEmbed.emit({ saved: true });
                return;
              }

              this.notificationService.show('success', saveMessage, 'OK');
              if (newOrderId) {
                this.router.navigate([
                  '/admin/patient',
                  this.patientId,
                  'order',
                  newOrderId,
                ]);
                return;
              }
              this.navigateBack({
                orderAdded: true,
                saveMessage,
              });
            },
            error: () => {
              this.notificationService.show(
                'error',
                'Could not create exchange job',
                'OK'
              );
            },
          });
      });
  }

  printCompletedOrder(): void {
    OrderDocument.printImages(
      this.orderFormGroup?.getRawValue()?.images || [],
      this.auth.getToken()
    );
  }

  emailCompletedOrder(): void {
    const order = this.orderFormGroup?.getRawValue();
    const invoiceNumber = String(order?.invoiceNumber || '').trim();
    const dialogRef = this.dialog.open(EmailOrderDialogComponent, {
      width: '420px',
      data: {
        email: this.patient?.email || '',
        invoiceNumber,
      },
    });
    dialogRef
      .afterClosed()
      .pipe(takeWhile((email) => !!email))
      .subscribe((email: string) => {
        const logItem = OrderDocument.emailLogItem(
          this.getLoggedInInitials(),
          this.auth.currentUser._id,
          email
        );
        this.orderService
          .updateOrderById(order._id, {
            log: [logItem],
            dateUpdated: new Date().toISOString(),
          })
          .subscribe({
            next: () => {
              const logs = this.orderFormGroup.get('log') as FormArray;
              logs?.insert(
                0,
                this.fb.group({
                  _id: logItem._id,
                  initials: logItem.initials,
                  user: logItem.user,
                  item: logItem.item,
                  notes: logItem.notes,
                  date: logItem.date,
                })
              );
              OrderDocument.openMailto(
                email,
                invoiceNumber,
                this.currentStore?.storeName
              );
              this.notificationService.show(
                'success',
                'Invoice emailed to customer',
                'OK'
              );
            },
            error: () => {
              this.notificationService.show(
                'error',
                'Could not record email in the LOG',
                'OK'
              );
            },
          });
      });
  }

  deleteCompletedOrder(): void {
    if (!this.isAdminUser) {
      this.notificationService.show(
        'error',
        'DELETE is limited to administrators',
        'OK'
      );
      return;
    }

    const orderId = this.orderFormGroup?.get('_id')?.value;
    if (!orderId) {
      return;
    }

    const dialogRef = this.dialog.open(DeleteOrderDialogComponent, {
      width: '420px',
      data: {
        invoiceNumber: this.orderFormGroup?.get('invoiceNumber')?.value,
      },
    });
    dialogRef
      .afterClosed()
      .pipe(takeWhile((result) => !!result?.reason))
      .subscribe((result: DeleteOrderDialogResult) => {
        this.orderService
          .deleteOrder(orderId, {
            reason: result.reason,
            notes: result.notes,
            initials: this.getLoggedInInitials(),
          })
          .subscribe(() => {
            this.navigateBack({
              saveMessage: this.getSaveSuccessMessage('DELETE'),
            });
          });
      });
  }

  createDuplicateFromDispensed(): void {
    const originalInvoice = String(
      this.orderFormGroup.get('invoiceNumber')?.value || ''
    ).trim();
    this.ensureInitialsFilled();
    const originalPayload = this.orderFormGroup.getRawValue();
    this.normalizeOrderMoneyFields(originalPayload);
    const initials =
      String(this.orderFormGroup.get('initials')?.value || '').trim() ||
      this.getLoggedInInitials();
    const quantity =
      parseInt(originalPayload?.products?.[0]?.quantity, 10) || 1;

    DuplicateJob.lookupCurrentProduct(
      this.productService,
      originalPayload?.products?.[0]
    )
      .pipe(
        switchMap((currentProduct) => {
          const inStock = DuplicateJob.isInStock(currentProduct);
          return this.orderService.reserveInvoiceNumber().pipe(
            switchMap((invoiceNumber) => {
              const newOrder = DuplicateJob.buildDuplicateOrder(
                originalPayload,
                originalInvoice,
                invoiceNumber,
                currentProduct,
                inStock,
                {
                  patientId: this.patientId,
                  userId: this.auth.currentUser._id,
                  store: this.auth.getStore(),
                  initials,
                }
              );
              return this.orderService.addOrder(newOrder).pipe(
                switchMap((createdOrders: Order[]) => {
                  const created = Array.isArray(createdOrders)
                    ? createdOrders[0]
                    : createdOrders;
                  if (!inStock || !currentProduct?._id) {
                    return of({ created, inStock });
                  }
                  return this.productService
                    .incrementProductInventoryQuantity(
                      currentProduct,
                      -Math.abs(quantity)
                    )
                    .pipe(
                      map(() => ({ created, inStock })),
                      catchError(() => {
                        this.notificationService.show(
                          'error',
                          'DUPLICATE CREATED; COULD NOT DEDUCT INVENTORY',
                          'OK'
                        );
                        return of({ created, inStock: false });
                      })
                    );
                })
              );
            })
          );
        })
      )
      .subscribe({
        next: ({ created, inStock }) => {
          const newOrderId = created?._id;
          const saveMessage = inStock
            ? this.getSaveSuccessMessage('DUPLICATE')
            : 'Duplicate job sent to TO ORDER with current pricing';

          if (this.embedded) {
            this.orderChangedInEmbed = true;
            this.notificationService.show('success', saveMessage, 'OK');
            this.closeEmbed.emit({ saved: true });
            return;
          }

          this.notificationService.show('success', saveMessage, 'OK');
          if (newOrderId) {
            this.router.navigate([
              '/admin/patient',
              this.patientId,
              'order',
              newOrderId,
            ]);
            return;
          }
          this.navigateBack({
            orderAdded: true,
            saveMessage,
          });
        },
        error: () => {
          this.notificationService.show(
            'error',
            'Could not create duplicate job',
            'OK'
          );
        },
      });
  }

  private promptRepairItems(onComplete: (items: string[] | null) => void): void {
    const dialogRef = this.dialog.open(RepairOrderDialogComponent, {
      width: '360px',
      data: {
        selected: this.orderFormGroup.get('products.0.frame.repairItems')?.value || [],
      },
    });
    dialogRef.afterClosed().subscribe((items: string[] | null) => {
      onComplete(items || null);
    });
  }

  createRepairFromDispensed(): void {
    const originalInvoice = String(
      this.orderFormGroup.get('invoiceNumber')?.value || ''
    ).trim();
    this.promptRepairItems((items) => {
      if (!items?.length) {
        return;
      }
      this.ensureInitialsFilled();
      const originalPayload = this.orderFormGroup.getRawValue();
      this.normalizeOrderMoneyFields(originalPayload);

      this.orderService
        .getPatientOrders(this.patientId, 'orders')
        .pipe(
          switchMap((orders: Order[]) => {
            const invoiceNumber = RepairJob.nextFxInvoice(
              originalInvoice,
              orders
            );
            const newOrder = RepairJob.buildLinkedRepairOrder(
              originalPayload,
              originalInvoice,
              invoiceNumber,
              items,
              {
                patientId: this.patientId,
                userId: this.auth.currentUser._id,
                store: this.auth.getStore(),
                initials:
                  String(this.orderFormGroup.get('initials')?.value || '').trim() ||
                  this.getLoggedInInitials(),
              }
            );
            return this.orderService.addOrder(newOrder);
          })
        )
        .subscribe((createdOrders: Order[]) => {
          const created = Array.isArray(createdOrders)
            ? createdOrders[0]
            : createdOrders;
          const newOrderId = created?._id;
          const saveMessage = this.getSaveSuccessMessage('REPAIR');

          if (this.embedded) {
            this.orderChangedInEmbed = true;
            this.notificationService.show('success', saveMessage, 'OK');
            this.closeEmbed.emit({ saved: true });
            return;
          }

          this.notificationService.show('success', saveMessage, 'OK');
          if (newOrderId) {
            this.router.navigate([
              '/admin/patient',
              this.patientId,
              'order',
              newOrderId,
            ]);
            return;
          }
          this.navigateBack({
            orderAdded: true,
            saveMessage,
          });
        });
    });
  }

  createRedoFromDispensed(): void {
    const originalInvoice = String(
      this.orderFormGroup.get('invoiceNumber')?.value || ''
    ).trim();
    const status = String(this.orderFormGroup.get('status')?.value || '');
    if (status !== 'DISPENSED') {
      this.notificationService.show(
        'error',
        'RE-DO is only available from a dispensed invoice',
        'OK'
      );
      return;
    }

    const dialogRef = this.dialog.open(RedoOrderDialogComponent, {
      width: '360px',
      data: { invoiceNumber: originalInvoice },
    });
    dialogRef.afterClosed().subscribe((reason: string | null) => {
      if (!reason) {
        return;
      }
      this.ensureInitialsFilled();
      const originalPayload = this.orderFormGroup.getRawValue();
      this.normalizeOrderMoneyFields(originalPayload);
      const initials =
        String(this.orderFormGroup.get('initials')?.value || '').trim() ||
        this.getLoggedInInitials();

      this.orderService
        .getPatientOrders(this.patientId, 'orders')
        .pipe(
          switchMap((orders: Order[]) =>
            RedoJob.create$(
              originalPayload,
              originalInvoice,
              orders,
              reason,
              {
                patientId: this.patientId,
                userId: this.auth.currentUser._id,
                store: this.auth.getStore(),
                initials,
              },
              this.orderService
            )
          )
        )
        .subscribe({
          next: (created: Order) => {
            const newOrderId = created?._id;
            const saveMessage = this.getSaveSuccessMessage('RE-DO');
            if (this.embedded) {
              this.orderChangedInEmbed = true;
              this.notificationService.show('success', saveMessage, 'OK');
              this.closeEmbed.emit({ saved: true });
              return;
            }
            this.notificationService.show('success', saveMessage, 'OK');
            if (newOrderId) {
              this.router.navigate([
                '/admin/patient',
                this.patientId,
                'order',
                newOrderId,
              ]);
              return;
            }
            this.navigateBack({
              orderAdded: true,
              saveMessage,
            });
          },
          error: () => {
            this.notificationService.show(
              'error',
              'Could not create RE-DO job',
              'OK'
            );
          },
        });
    });
  }

  createNewRxFromDispensed(): void {
    const originalInvoice = String(
      this.orderFormGroup.get('invoiceNumber')?.value || ''
    ).trim();
    const status = String(this.orderFormGroup.get('status')?.value || '');
    if (status !== 'DISPENSED') {
      this.notificationService.show(
        'error',
        'NEW RX is only available from a dispensed invoice',
        'OK'
      );
      return;
    }

    this.ensureInitialsFilled();
    const originalPayload = this.orderFormGroup.getRawValue();
    this.normalizeOrderMoneyFields(originalPayload);
    const initials =
      String(this.orderFormGroup.get('initials')?.value || '').trim() ||
      this.getLoggedInInitials();

    this.orderService
      .getPatientOrders(this.patientId, 'orders')
      .pipe(
        switchMap((orders: Order[]) =>
          NewRxJob.create$(
            originalPayload,
            originalInvoice,
            orders,
            {
              patientId: this.patientId,
              userId: this.auth.currentUser._id,
              store: this.auth.getStore(),
              initials,
            },
            this.orderService
          )
        )
      )
      .subscribe({
        next: ({ created, taxCredit }) => {
          const newOrderId = created?._id;
          const saveMessage = taxCredit
            ? 'New RX job created; previous Frame Only sales tax credited'
            : this.getSaveSuccessMessage('NEW RX');
          const go = () => {
            if (this.embedded) {
              this.orderChangedInEmbed = true;
              this.notificationService.show('success', saveMessage, 'OK');
              this.closeEmbed.emit({ saved: true });
              return;
            }

            this.notificationService.show('success', saveMessage, 'OK');
            if (newOrderId) {
              this.router.navigate([
                '/admin/patient',
                this.patientId,
                'order',
                newOrderId,
              ]);
              return;
            }
            this.navigateBack({
              orderAdded: true,
              saveMessage,
            });
          };
          if (taxCredit > 0 && newOrderId) {
            this.paymentService
              .applyTaxAdjustment({
                orderId: newOrderId,
                amount: taxCredit,
                sourceInvoice: originalInvoice,
                initials,
                user: this.auth.currentUser._id,
                notes: 'Frame Only → first RX tax credit',
              })
              .subscribe({
                next: () => go(),
                error: () => go(),
              });
            return;
          }
          go();
        },
        error: () => {
          this.notificationService.show(
            'error',
            'Could not create NEW RX job',
            'OK'
          );
        },
      });
  }

  restyleOrder(): void {
    const originalInvoice = String(
      this.orderFormGroup.get('invoiceNumber')?.value || ''
    ).trim();
    const dialogRef = this.dialog.open(RestyleOrderDialogComponent, {
      width: '420px',
      data: {
        invoiceNumber: originalInvoice,
      },
    });

    dialogRef
      .afterClosed()
      .pipe(takeWhile((result) => !!result))
      .subscribe(() => {
        this.ensureInitialsFilled();

        const logItem = new LogElement();
        logItem.initials =
          String(this.orderFormGroup.get('initials')?.value || '').trim() ||
          this.getLoggedInInitials();
        logItem.user = this.auth.currentUser._id;
        logItem.item = 'RE-STYLED';
        logItem.notes = '';
        logItem.date = Date.now().toString();
        this.prependLogEntry(this.orderFormGroup, logItem);

        this.orderFormGroup.get('status').setValue('RE-STYLED');
        this.orderFormGroup.get('todo').setValue('RE-STYLED');

        const originalPayload = this.orderFormGroup.getRawValue();
        this.normalizeOrderMoneyFields(originalPayload);
        const newOrder = this.buildRestyleOrder(originalPayload, originalInvoice);

        this.orderService
          .updateOrderFull(originalPayload)
          .pipe(
            switchMap(() => {
              newOrder.invoiceNumber = this.nextRestyleInvoice(
                originalInvoice,
                []
              );
              return this.orderService.addOrder(newOrder);
            })
          )
          .subscribe((createdOrders: Order[]) => {
            const created = Array.isArray(createdOrders)
              ? createdOrders[0]
              : createdOrders;
            const newOrderId = created?._id;
            const frameCredit = FinancialCalculator.money(
              originalPayload?.products?.[0]?.retail
            );
            const afterCredit = (done: () => void) => {
              if (frameCredit > 0 && newOrderId) {
                this.paymentService
                  .applyStructuredCredit({
                    orderId: newOrderId,
                    type: 'Restyle Credit',
                    amount: frameCredit,
                    sourceInvoice: originalInvoice,
                    initials: this.getLoggedInInitials(),
                    user: this.auth.currentUser._id,
                    notes: `Frame credit from ${originalInvoice}`,
                  })
                  .subscribe({
                    next: () => done(),
                    error: () => done(),
                  });
                return;
              }
              done();
            };
            const saveMessage = this.getSaveSuccessMessage('RE-STYLE');

            afterCredit(() => {
            if (this.embedded) {
              this.orderChangedInEmbed = true;
              this.notificationService.show('success', saveMessage, 'OK');
              this.closeEmbed.emit({ saved: true });
              return;
            }

            this.notificationService.show('success', saveMessage, 'OK');

            if (newOrderId) {
              this.router.navigate([
                '/admin/patient',
                this.patientId,
                'order',
                newOrderId,
              ]);
              return;
            }

            this.navigateBack({
              orderAdded: true,
              saveMessage,
            });
            });
          });
      });
  }

  private buildRestyleOrder(original: any, originalInvoice: string): Order {
    const clone = JSON.parse(JSON.stringify(original || {}));
    clone._id = '';
    clone.invoiceNumber = '';
    clone.status = this.getStackOrderStatusForType(clone.type || 'O');
    clone.todo = 'ORDER';
    clone.date = new Date();
    clone.dateAdded = undefined;
    clone.dateUpdated = undefined;
    clone.notes = '';
    clone.vendorOrder = null;
    clone.patient = this.patientId;
    clone.user = this.auth.currentUser._id;
    clone.store = this.auth.getStore();
    delete clone.initials;

    (clone.products || []).forEach((productElement: any) => {
      productElement._id = null;
    });

    const logItem = new LogElement();
    logItem.initials =
      String(original?.initials || '').trim() || this.getLoggedInInitials();
    logItem.user = this.auth.currentUser._id;
    logItem.item = originalInvoice
      ? `RE-STYLE FROM ${originalInvoice}`
      : 'RE-STYLE';
    logItem.notes = '';
    logItem.date = Date.now().toString();
    clone.log = [logItem];
    clone.linkedInvoice = originalInvoice;
    clone.financialEvents = [];
    clone.acknowledgedAt = '';
    clone.acknowledgment = null;
    clone.paidInFullDate = '';
    clone.dispensedDate = '';
    clone.newBusinessDate = new Date();

    this.normalizeOrderMoneyFields(clone);
    return clone as Order;
  }

  private nextRestyleInvoice(originalInvoice: string, orders: Order[]): string {
    const prefix = String(originalInvoice || '')
      .trim()
      .replace(/-RS\d*$/i, '');
    if (!prefix) {
      return '';
    }
    const escaped = prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const pattern = new RegExp(`^${escaped}-RS(\\d+)?$`, 'i');
    let max = 0;
    let hasBase = false;
    (orders || []).forEach((order) => {
      const invoice = String(order?.invoiceNumber || '').trim();
      const match = invoice.match(pattern);
      if (!match) {
        return;
      }
      hasBase = true;
      const n = match[1] ? parseInt(match[1], 10) : 1;
      if (!isNaN(n) && n > max) {
        max = n;
      }
    });
    if (!hasBase) {
      return `${prefix}-RS`;
    }
    return `${prefix}-RS${max + 1}`;
  }

  private normalizeOrderMoneyFields(order: any): void {
    const products = order?.products;
    if (!Array.isArray(products)) {
      return;
    }
    products.forEach((productElement: any) => {
      if (typeof productElement?.cost === 'string') {
        productElement.cost = parseFloat(productElement.cost.replace(/,/g, '')) || 0;
      }
      if (typeof productElement?.retail === 'string') {
        productElement.retail =
          parseFloat(productElement.retail.replace(/,/g, '')) || 0;
      }
      if (typeof productElement?.tax === 'string') {
        productElement.tax = parseFloat(productElement.tax.replace(/,/g, '')) || 0;
      }
    });
  }
}
